import React, { useRef, useState, useLayoutEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Tooltip } from '../../Tooltip';
import { debounce, generateUniqueId } from '../../Utils/helper';
import { NameTypes } from '../helper';
import styles from '@dtsl/css-data-table/dist/index.module.css';
import ArrowDown from '@dtsl/icons/dist/icons/react/ArrowDown';
function Column(props) {
  const columnRef = useRef(null);
  const [columnTooltipVisible, setColumnTooltipVisible] = useState(false);
  const {
    col,
    columns,
    headerIndex,
    onColumnItemSelect
  } = props;
  const {
    name,
    width = name === NameTypes.ACTION ? 150 : 100,
    type = '',
    sortable = false,
    masterColumn = false,
    headerMenuItems = [],
    headerCellRenderer: HeaderCellRenderer,
    colStyles = {}
  } = col;
  const uniqueId = typeof name === 'string' ? `colName_${name?.replace(/\s/g, '')}` : `colName_${generateUniqueId()}`;
  const checkOverflow = () => {
    const ele = document.getElementById(uniqueId);
    if (ele) {
      setColumnTooltipVisible(+ele?.offsetWidth < +ele?.scrollWidth);
    }
  };
  const {
    execDebounce: handleResize,
    cancelDebounce
  } = debounce(checkOverflow, 300);
  useLayoutEffect(() => {
    checkOverflow();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelDebounce();
    };
  }, [columnRef.current]);
  const sortMenuItem = headerMenuItems.find(menuItem => menuItem.type === 'SORT') || {};
  const onSort = event => {
    event.stopPropagation();
    if (sortable) {
      onColumnItemSelect({
        item: sortMenuItem,
        headerCell: col,
        headerCellIndex: headerIndex,
        columns
      });
    }
  };
  const columnHeadingClasses = classNames({
    [styles['header-cell']]: true,
    [styles['leading-cell']]: masterColumn,
    [styles['text-align-right']]: type === 'number',
    [styles['text-align-left']]: type !== 'number',
    [styles['sib-is-sortable']]: sortable
  });
  const sortArrowClasses = classNames({
    [styles['sib-sorting-icon']]: true,
    [styles['display-icon-up']]: sortMenuItem.value === 'ascending',
    [styles['display-icon-down']]: sortMenuItem.value === 'descending'
  });
  const getColumnContent = () => {
    const content = HeaderCellRenderer ? /*#__PURE__*/React.createElement(HeaderCellRenderer, {
      headerCellIndex: headerIndex,
      headerCell: col,
      columns: columns,
      onColumnItemSelect: onColumnItemSelect,
      item: sortMenuItem
    }) : /*#__PURE__*/React.createElement("div", {
      id: uniqueId,
      ref: columnRef,
      className: styles['text-truncate']
    }, name);
    if (columnTooltipVisible) {
      return /*#__PURE__*/React.createElement(Tooltip, {
        text: name
      }, content);
    }
    return content;
  };
  const headerCellContent = HeaderCellRenderer ? /*#__PURE__*/React.createElement(HeaderCellRenderer, {
    headerCellIndex: headerIndex,
    headerCell: col,
    columns: columns,
    onColumnItemSelect: onColumnItemSelect,
    item: sortMenuItem
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, getColumnContent(), sortable && /*#__PURE__*/React.createElement("span", {
    className: styles[`sib-sorting-icon-container`],
    tabIndex: sortable ? 0 : -1,
    role: "button"
  }, /*#__PURE__*/React.createElement(ArrowDown, {
    className: sortArrowClasses
  })));
  return /*#__PURE__*/React.createElement("th", {
    "aria-sort": sortMenuItem.value || 'none',
    className: columnHeadingClasses,
    onClick: onSort,
    onKeyDown: event => {
      if (event.keyCode === 13) {
        onSort(event);
      }
    },
    style: {
      flex: name === NameTypes.ACTION ? null : `1 0 ${width}px`,
      width: width + 'px',
      minWidth: width + 'px',
      ...colStyles
    }
  }, headerCellContent);
}
export default Column;
Column.propTypes = {
  col: PropTypes.object.isRequired,
  headerIndex: PropTypes.number.isRequired,
  checkboxSelection: PropTypes.bool.isRequired,
  onColumnItemSelect: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired
};